import React, { useEffect, useState } from 'react';
import { Star, Loader2, Send, CheckCircle, Clock } from 'lucide-react';
import { Button } from './ui/button';
import { ordersAPI, settingsAPI } from '../services/api';

const fmt = (d) => new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });

const ReviewReminderPanel = ({ order, onDone }) => {
  const [days, setDays] = useState(null);
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => { settingsAPI.getCompany().then(s => setDays(s.review_reminder_days ?? 10)).catch(() => setDays(10)); }, []);

  if (order.status !== 'Livré') return null;
  const sentAt = order.review_reminder_sent_at;
  const dueAt = order.delivered_at && days !== null ? new Date(new Date(order.delivered_at).getTime() + days * 86400000) : null;

  const send = async () => {
    if (sentAt && !window.confirm('Un rappel a déjà été envoyé pour cette commande. Renvoyer l\'e-mail au client ?')) return;
    setSending(true);
    setMessage(null);
    try {
      const res = await ordersAPI.sendReviewReminder(order.id);
      setMessage({ type: 'success', text: res.message });
      onDone && onDone();
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.detail || 'Envoi impossible' });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="border border-yellow-200 bg-yellow-50/60 rounded-lg p-4" data-testid="review-reminder-panel">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[200px]">
          <p className="font-semibold text-gray-900 flex items-center gap-2 text-sm"><Star size={16} className="text-yellow-500 fill-yellow-400" /> Rappel d'avis client</p>
          <p className="text-xs text-gray-600 mt-1" data-testid="review-reminder-status">
            {sentAt ? (
              order.review_reminder_skipped
                ? <span className="inline-flex items-center gap-1 text-gray-600"><CheckCircle size={12} /> Aucun rappel nécessaire : {order.review_reminder_skipped.toLowerCase()}</span>
                : <span className="inline-flex items-center gap-1 text-green-700"><CheckCircle size={12} /> Rappel envoyé le {fmt(sentAt)}{order.review_reminder_count > 1 ? ` (${order.review_reminder_count} envois)` : ''}{order.review_reminder_2_sent_at && !order.review_reminder_2_skipped ? <span data-testid="review-reminder-2-status"> · dernier rappel le {fmt(order.review_reminder_2_sent_at)}</span> : order.review_reminder_2_skipped ? <span data-testid="review-reminder-2-status" className="text-gray-600"> · 2e rappel inutile : {order.review_reminder_2_skipped.toLowerCase()}</span> : null}</span>
            ) : (
              <span className="inline-flex items-center gap-1"><Clock size={12} /> Envoi automatique prévu {dueAt ? `le ${fmt(dueAt)}` : `${days ?? 10} jours après la livraison`}{order.delivered_at ? ` (livrée le ${fmt(order.delivered_at)})` : ''}</span>
            )}
          </p>
        </div>
        <Button data-testid="review-reminder-send" size="sm" variant="outline" onClick={send} disabled={sending} className="border-yellow-400 text-yellow-800 hover:bg-yellow-100">
          {sending ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Send size={14} className="mr-2" />} {sentAt && !order.review_reminder_skipped ? 'Renvoyer le rappel' : 'Envoyer le rappel maintenant'}
        </Button>
      </div>
      {message && <p className={`text-xs mt-2 ${message.type === 'error' ? 'text-red-600' : 'text-green-700'}`} data-testid="review-reminder-message">{message.text}</p>}
    </div>
  );
};

export default ReviewReminderPanel;
